import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Monitor = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [currentTime, setCurrentTime] = useState(new Date());

  // 模拟实时数据
  const [systemData] = useState({
    availability: 98.5,
    uptime: '24/7',
    onlineDevices: 156,
    warningDevices: 2
  });

  const [deviceStatus] = useState([
    {
      id: 1,
      name: '主电机 #1',
      status: 'normal',
      temperature: 65,
      vibration: 2.3,
      health: 98
    },
    {
      id: 2,
      name: '减速机 #2',
      status: 'warning',
      temperature: 78,
      vibration: 4.1,
      health: 85
    },
    {
      id: 3,
      name: '驱动滚筒 #3',
      status: 'normal',
      temperature: 58,
      vibration: 1.8,
      health: 96
    }
  ]);

  // 模拟图表数据
  const chartData = [
    { time: '00:00', temp: 62, vibration: 2.1 },
    { time: '04:00', temp: 64, vibration: 2.2 },
    { time: '08:00', temp: 66, vibration: 2.3 },
    { time: '12:00', temp: 68, vibration: 2.4 },
    { time: '16:00', temp: 65, vibration: 2.3 },
    { time: '20:00', temp: 63, vibration: 2.2 },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleQuickAction = (action) => {
    switch (action) {
      case '数据分析':
        navigate('/analysis');
        break;
      case '故障诊断':
        navigate('/diagnosis');
        break;
      case '报警设置':
        navigate('/alarm');
        break;
      case '设备配置':
        navigate('/device');
        break;
      default:
        showToast(`${action}功能已激活`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return 'text-success-600';
      case 'warning': return 'text-warning-600';
      case 'error': return 'text-danger-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'normal': return 'bg-success-50 border-success-200';
      case 'warning': return 'bg-warning-50 border-warning-200';
      case 'error': return 'bg-danger-50 border-danger-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="pb-4">
      {/* 顶部状态栏 */}
      <div className="phm-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">设备监控中心</h1>
            <p className="text-sm opacity-90">实时监控 · 智能预警</p>
          </div>
          <div className="text-right">
            <div className="text-xs opacity-80">最后更新</div>
            <div className="text-sm font-semibold">
              {currentTime.toLocaleTimeString('zh-CN', { hour12: false })}
            </div>
          </div>
        </div>
      </div>

      {/* 系统状态概览 */}
      <div className="phm-status-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-800">系统运行状态</h3>
          <span className="px-2 py-1 bg-success-100 text-success-800 text-xs rounded-full">
            正常运行
          </span>
        </div>
        <div className="phm-metric-grid">
          <div className="phm-metric-item">
            <div className="phm-metric-value text-success-600">{systemData.availability}%</div>
            <div className="phm-metric-label">系统可用性</div>
          </div>
          <div className="phm-metric-item">
            <div className="phm-metric-value text-primary-600">{systemData.uptime}</div>
            <div className="phm-metric-label">运行时间</div>
          </div>
          <div className="phm-metric-item">
            <div className="phm-metric-value text-purple-600">{systemData.onlineDevices}</div>
            <div className="phm-metric-label">在线设备</div>
          </div>
          <div className="phm-metric-item">
            <div className="phm-metric-value text-warning-600">{systemData.warningDevices}</div>
            <div className="phm-metric-label">预警设备</div>
          </div>
        </div>
      </div>

      {/* 关键设备状态 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">关键设备状态</h3>
        <div className="space-y-3">
          {deviceStatus.map((device) => (
            <div key={device.id} className={`flex items-center justify-between p-3 rounded-lg border ${getStatusBg(device.status)}`}>
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${device.status === 'normal' ? 'bg-success-500' : device.status === 'warning' ? 'bg-warning-500' : 'bg-danger-500'}`}></div>
                <div>
                  <div className="font-medium text-gray-800">{device.name}</div>
                  <div className="text-sm text-gray-500">
                    温度: {device.temperature}°C | 振动: {device.vibration}mm/s
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className={`text-sm font-semibold ${getStatusColor(device.status)}`}>
                  {device.status === 'normal' ? '正常' : device.status === 'warning' ? '注意' : '故障'}
                </div>
                <div className="text-xs text-gray-500">{device.health}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 实时数据流 */}
      <div className="phm-chart-container">
        <h3 className="font-semibold text-gray-800 mb-3">实时数据流</h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="temp" stroke="#3b82f6" strokeWidth={2} name="温度" />
              <Line type="monotone" dataKey="vibration" stroke="#10b981" strokeWidth={2} name="振动" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 快速操作 */}
      <div className="phm-status-card">
        <h3 className="font-semibold text-gray-800 mb-3">快速操作</h3>
        <div className="grid grid-cols-2 gap-3">
          <button 
            className="flex items-center justify-center space-x-2 p-3 bg-primary-50 text-primary-700 rounded-lg border border-primary-200 hover:bg-primary-100 transition-colors"
            onClick={() => handleQuickAction('数据分析')}
          >
            <i className="fas fa-chart-area"></i>
            <span className="text-sm font-medium">数据分析</span>
          </button>
          <button 
            className="flex items-center justify-center space-x-2 p-3 bg-success-50 text-success-700 rounded-lg border border-success-200 hover:bg-success-100 transition-colors"
            onClick={() => handleQuickAction('故障诊断')}
          >
            <i className="fas fa-stethoscope"></i>
            <span className="text-sm font-medium">故障诊断</span>
          </button>
          <button 
            className="flex items-center justify-center space-x-2 p-3 bg-warning-50 text-warning-700 rounded-lg border border-warning-200 hover:bg-warning-100 transition-colors"
            onClick={() => handleQuickAction('报警设置')}
          >
            <i className="fas fa-exclamation-triangle"></i>
            <span className="text-sm font-medium">报警设置</span>
          </button>
          <button 
            className="flex items-center justify-center space-x-2 p-3 bg-purple-50 text-purple-700 rounded-lg border border-purple-200 hover:bg-purple-100 transition-colors"
            onClick={() => handleQuickAction('设备配置')}
          >
            <i className="fas fa-cogs"></i>
            <span className="text-sm font-medium">设备配置</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Monitor;
